import { useApolloClient } from '@apollo/client'
import { buttonColorVariantSchema, buttonConfigurationSchema, getWebPath } from '@doss/common-all'
import { ChevronDown, Loader } from 'lucide-react'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { MenuDropdown, type MenuDropdownItem } from '~/@/components/inputs/selection/menu-dropdown'
import { Button } from '~/@/components/ui/button'
import {
  ActionStatus,
  BreadcrumbObjectType,
  WorkflowStatus,
  useExecuteActionManualTriggerMutation,
  usePushRowButtonNodesSubscription,
} from '~/common/generated/apollo-operations'
import { usePage } from '~/common/hooks/usePage'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { ConfirmationDialog } from '~/components/ConfirmationDialog'
import { DossIcon } from '~/components/DossIcon'
import {
  RecordDetailTableRow,
  RecordDetailTableRowWorkspaceButton,
} from '~/components/records/types'
import { WorkspaceButton } from './WorkspaceButton'

interface WorkspaceButtonsDisplayProps {
  tableRow: RecordDetailTableRow
}

const MAX_VISIBLE_BUTTONS = 3

export function WorkspaceButtonsDisplay({ tableRow }: WorkspaceButtonsDisplayProps) {
  const navigate = useNavigate()
  const { pageType } = usePage()
  const { t } = useTypedTranslation()
  const [confirmationState, setConfirmationState] = useState<{
    open: boolean
    button: RecordDetailTableRowWorkspaceButton | null
  }>({ open: false, button: null })

  const apolloClient = useApolloClient()
  const [executeTrigger] = useExecuteActionManualTriggerMutation()

  // Writes the row's processing button into the cache. The pushRowButtonNodes subscription is
  // the single source of truth for clearing this state once an action ran: a fast-completing
  // action can publish its clear event before the mutation response reaches the client, so the
  // state is set once when the action is triggered and never re-asserted afterwards (INC-1970).
  const writeProcessingButton = useCallback(
    (button: { id: string; name: string } | null) => {
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'TableRow', id: tableRow.id }),
        fields: {
          processingWorkspaceButton() {
            return button
              ? { __typename: 'WorkspaceButton' as const, id: button.id, name: button.name }
              : null
          },
        },
      })
    },
    [apolloClient, tableRow.id],
  )

  // Subscribe to row button updates
  usePushRowButtonNodesSubscription({
    variables: { input: { rowId: tableRow.id } },
    skip: pageType !== 'record',
    onData: ({ client, data }) => {
      const pushData = data.data?.pushRowButtonNodes
      if (!pushData) {
        return
      }

      client.cache.modify({
        id: client.cache.identify({ __typename: 'TableRow', id: tableRow.id }),
        fields: {
          processingWorkspaceButton() {
            return pushData.processingWorkspaceButton
          },
          currentButtonNodes() {
            return pushData.currentButtonNodes
          },
        },
      })
    },
  })

  // Get buttons from current button nodes
  const allButtons = tableRow.currentButtonNodes
    .flatMap((node) => node.workspaceButtons)
    .sort((a, b) => a.order - b.order)

  const processingButton = useMemo(() => {
    return allButtons.find((button) => button.id === tableRow.processingWorkspaceButton?.id)
  }, [allButtons, tableRow.processingWorkspaceButton?.id])

  const isProcessingButton = useCallback(
    (button: RecordDetailTableRowWorkspaceButton) => {
      return button.id === processingButton?.id
    },
    [processingButton],
  )

  if (allButtons.length === 0) {
    return null
  }

  const visibleButtons = allButtons.slice(0, MAX_VISIBLE_BUTTONS)
  const hiddenButtons = allButtons.slice(MAX_VISIBLE_BUTTONS)

  const handleClickAction = async (button: RecordDetailTableRowWorkspaceButton) => {
    if (button.manualTrigger) {
      writeProcessingButton(button)
      try {
        await executeTrigger({
          variables: {
            input: {
              actionManualTriggerId: button.manualTrigger.id,
              workspaceButtonId: button.id,
              rowId: tableRow.id,
            },
          },
        })
      } catch {
        // Nothing was triggered, so no clear event will arrive — remove the spinner.
        writeProcessingButton(null)
      }
    } else if (button.form || button.formV2) {
      const formId = button.form?.id || button.formV2?.id
      const path = getWebPath({ objectType: BreadcrumbObjectType.FORM, objectId: formId })
      navigate(path)
    }
  }

  const handleButtonClick = async (button: RecordDetailTableRowWorkspaceButton) => {
    if (processingButton) {
      return
    }

    await handleClickAction(button)
  }

  const isButtonInactive = (button: RecordDetailTableRowWorkspaceButton): boolean => {
    if (!button.manualTrigger) {
      return false
    }
    return (
      button.manualTrigger.action?.status === ActionStatus.INACTIVE ||
      button.manualTrigger.action?.workflow.status === WorkflowStatus.INACTIVE
    )
  }

  // Helper to check if button is destructive
  const isButtonDestructive = (button: RecordDetailTableRowWorkspaceButton) => {
    if (!button.buttonConfiguration) {
      return false
    }

    const configToParse =
      typeof button.buttonConfiguration === 'string'
        ? JSON.parse(button.buttonConfiguration)
        : button.buttonConfiguration

    const parseResult = buttonConfigurationSchema.safeParse(configToParse)
    return parseResult.success
      ? parseResult.data.colorVariant === buttonColorVariantSchema.enum.DESTRUCTIVE
      : false
  }

  const handleDropdownItemClick = (button: RecordDetailTableRowWorkspaceButton) => {
    if (button.requiresConfirmation) {
      setConfirmationState({ open: true, button })
    } else {
      handleButtonClick(button)
    }
  }

  const executeConfirmedButton = async () => {
    if (!confirmationState.button) {
      return
    }

    // Close the modal immediately when user confirms
    setConfirmationState({ open: false, button: null })

    // Then execute the button action
    await handleButtonClick(confirmationState.button)
  }

  const isDisabled = !!processingButton

  return (
    <>
      <div className="flex items-center gap-1">
        {/* Visible buttons */}
        {visibleButtons.map((button) => {
          return (
            <WorkspaceButton
              key={button.id}
              button={button}
              disabled={isDisabled || isButtonInactive(button)}
              isProcessing={isProcessingButton(button)}
              onExecute={handleButtonClick}
            />
          )
        })}

        {/* Dropdown for additional buttons */}
        {hiddenButtons.length > 0 && (
          <MenuDropdown
            align="end"
            items={hiddenButtons.map((button): MenuDropdownItem => {
              const isDestructive = isButtonDestructive(button)
              let icon: ReactNode | undefined
              if (isProcessingButton(button)) {
                icon = <Loader className="size-3 animate-spin [animation-duration:2s]" />
              } else if (button.icon) {
                icon = <DossIcon name={button.icon.name} size={12} />
              }

              return {
                label: button.name,
                icon,
                destructive: isDestructive,
                disabled: isDisabled || isButtonInactive(button),
                onClick: () => handleDropdownItemClick(button),
                className: 'mx-1 rounded',
              }
            })}
            menuClassName="px-1"
            sideOffset={12}
            trigger={
              <Button disabled={isDisabled} size="xs" variant="outline">
                {t('table_settings.button_node.more_buttons', { count: hiddenButtons.length })}
                <ChevronDown className="ms-1 size-3" />
              </Button>
            }
          />
        )}
      </div>

      {/* Confirmation dialog for dropdown items */}
      {confirmationState.button && (
        <ConfirmationDialog
          checkboxText={
            confirmationState.button.requiresConfirmation
              ? (confirmationState.button.confirmationMessage ??
                t('table_settings.button_node.confirmation_message_placeholder'))
              : undefined
          }
          confirmLabel={t('common.actions.confirm')}
          description={confirmationState.button.manualTrigger?.action?.description ?? undefined}
          isLoading={isProcessingButton(confirmationState.button)}
          isOpen={confirmationState.open}
          onClose={() => setConfirmationState({ open: false, button: null })}
          onConfirm={executeConfirmedButton}
          title={confirmationState.button.name}
          variant={isButtonDestructive(confirmationState.button) ? 'destructive' : 'default'}
        />
      )}
    </>
  )
}
